'use strict';
const css = require('../dom/css'),
	px = require('../dom/px');

module.exports = function Stage(domElement, viewport, themeSource, options) {
	if (!domElement || !viewport || !themeSource) {
		throw new Error('invalid-args');
	}
	const svgContainer = domElement.querySelector('[data-mapjs-role=svg-container]'),
		scrollbarSize = (options && options.scrollbarSize) || 20,
		stageDataFromViewPort = () => {
			const viewPortDimensions = viewport.getDimensions();
			return {
				offsetX: viewPortDimensions.width / 2,
				offsetY: viewPortDimensions.height / 2,
				width: viewPortDimensions.width - scrollbarSize,
				height: viewPortDimensions.height - scrollbarSize,
				scale: 1
			};
		},
		stageData = stageDataFromViewPort(),
		stageMargin = (options && options.stageMargin) || {top: 0, left: 0, bottom: 0, right: 0},
		stageToViewCoordinates = function (x, y) {
			const scrollPosition = viewport.getDimensions();
			return {
				x: stageData.scale * (x + stageData.offsetX) - scrollPosition.left,
				y: stageData.scale * (y + stageData.offsetY) - scrollPosition.top
			};
		},
		viewToStageCoordinates = function (x, y) {
			const scrollPosition = viewport.getDimensions();
			return {
				x: (scrollPosition.left + x) / stageData.scale - stageData.offsetX,
				y: (scrollPosition.top + y) / stageData.scale - stageData.offsetY
			};
		},
		toCSSBackground = (canvasTheme) => {
			const components = [];
			if (canvasTheme?.icon?.url) {
				const translatedUrl = (options?.resourceTranslator && options.resourceTranslator(canvasTheme.icon.url)) || canvasTheme.icon.url;
				components.push('background-image: url(' + translatedUrl + ')');
				components.push('background-repeat: repeat');
				components.push('background-position: center center');
			} else if (canvasTheme.backgroundColor) {
				components.push('background-color:' + canvasTheme.backgroundColor);
			}
			return components.join(';');
		},
		updateStage = function () {
			const size = {
					'minWidth': px(Math.round(stageData.width - stageData.offsetX)),
					'minHeight': px(Math.round(stageData.height - stageData.offsetY)),
					'width': px(Math.round(stageData.width - stageData.offsetX)),
					'height': px(Math.round(stageData.height - stageData.offsetY)),
					'transformOrigin': 'top left'
				},
				theme = themeSource(),
				stylingProperties = theme && toCSSBackground(theme.canvasTheme());
			if (stageData.scale === 1) {
				size.transform = 'translate3d(' + px(Math.round(stageData.offsetX)) + ', ' + px(Math.round(stageData.offsetY)) + ', 0)';
			} else {
				size.transform = 'scale(' + stageData.scale + ') translate(' + px(Math.round(stageData.offsetX)) + ', ' + px(Math.round(stageData.offsetY)) + ')';
			}
			css(domElement, size);
			svgContainer.setAttribute('viewBox',
				'' + Math.round(-1 * stageData.offsetX) + ' ' +  Math.round(-1 * stageData.offsetY) + ' ' + Math.round(stageData.width) + ' ' + Math.round(stageData.height)
			);
			svgContainer.setAttribute('style',
				'top:' + Math.round(-1 * stageData.offsetY) + 'px; ' +
				'left:' + Math.round(-1 * stageData.offsetX) + 'px; ' +
				'width:' +  Math.round(stageData.width) + 'px; ' +
				'height:' +  Math.round(stageData.height) + 'px;' +
				stylingProperties
			);
		},
		ensureSpaceForPoint = function (x, y) {/* in stage coordinates */
			let dirty = false;
			if (x < -1 * stageData.offsetX) {
				stageData.width =  stageData.width - stageData.offsetX - x;
				stageData.offsetX = -1 * x;
				dirty = true;
			}
			if (y < -1 * stageData.offsetY) {
				stageData.height = stageData.height - stageData.offsetY - y;
				stageData.offsetY = -1 * y;
				dirty = true;
			}
			if (x > stageData.width - stageData.offsetX) {
				stageData.width = stageData.offsetX + x;
				dirty = true;
			}
			if (y > stageData.height - stageData.offsetY) {
				stageData.height = stageData.offsetY + y;
				dirty = true;
			}
			if (dirty) {
				updateStage();
			}
		},
		centerViewOn = function (x, y, animate) { /*in the stage coordinate system*/
			const viewPortCenter = viewport.getCenter();
			let newLeftScroll = false, newTopScroll = false;

			ensureSpaceForPoint(x - viewPortCenter.x / stageData.scale, y - viewPortCenter.y / stageData.scale);
			ensureSpaceForPoint(x + viewPortCenter.x / stageData.scale, y + viewPortCenter.y / stageData.scale);

			newLeftScroll = stageData.scale * (x + stageData.offsetX) - viewPortCenter.x;
			newTopScroll = stageData.scale * (y + stageData.offsetY) - viewPortCenter.y;
			viewport.setPosition(newLeftScroll, newTopScroll, animate);
		},
		stagePointAtViewportCenter = function () {
			const viewportCenter = viewport.getCenter();
			return viewToStageCoordinates(viewportCenter.x, viewportCenter.y);
		},
		stagePositionForPointEvent = function (evt) {
			const viewportDropCoordinates = viewport.coordinatesForPointEvent(evt);
			if (viewportDropCoordinates) {
				return viewToStageCoordinates(viewportDropCoordinates.x, viewportDropCoordinates.y);
			}
		},
		ensureBoxVisible = (box) => {
			const nodeTopLeft = stageToViewCoordinates(box.x, box.y),
				nodeBottomRight = stageToViewCoordinates(box.x + box.width, box.y + box.height),
				viewDimensions = viewport.getDimensions(),
				margin = {top: 10, left: 10, bottom: 10, right: 10};
			viewport.finishScrolling();
			let newLeft =  viewDimensions.left, newTop = viewDimensions.top;
			if ((nodeTopLeft.x - margin.left) < 0) {
				newLeft = viewDimensions.left + nodeTopLeft.x - margin.left;
			} else if ((nodeBottomRight.x + margin.right) > viewDimensions.width) {
				newLeft = viewDimensions.left + nodeBottomRight.x - viewDimensions.width + margin.right;
			}
			if ((nodeTopLeft.y - margin.top) < 0) {
				newTop = viewDimensions.top + nodeTopLeft.y - margin.top;
			} else if ((nodeBottomRight.y + margin.bottom) > viewDimensions.height) {
				newTop = viewDimensions.top + nodeBottomRight.y - viewDimensions.height + margin.bottom;
			}
			if (newLeft !== viewDimensions.left || newTop !== viewDimensions.top) {
				viewport.setPosition(newLeft, newTop, true);
			}
		},
		ensureSpaceForBox = (node) => {
			/* sequence of calculations is important because maxX and maxY take into consideration the new offsetX snd offsetY */
			ensureSpaceForPoint(node.x - stageMargin.left, node.y - stageMargin.top);
			ensureSpaceForPoint(node.x + node.width + stageMargin.right, node.y + node.height + stageMargin.bottom);
		},
		rescale = (scaleMultiplier) => {
			const currentScale = stageData.scale,
				targetScale = Math.max(Math.min(currentScale * scaleMultiplier, 5), 0.2),
				currentCenter = stagePointAtViewportCenter();
			if (currentScale === targetScale) {
				return;
			}
			stageData.scale = targetScale;
			updateStage();
			centerViewOn(currentCenter.x, currentCenter.y);
		},
		setScale = (targetScale) => {
			if (stageData.scale === targetScale) {
				return;
			}
			stageData.scale = targetScale;
			updateStage();
		},
		reset = () => {
			stageData.scale = 1;
			stageData.height = 0;
			stageData.width = 0;
			stageData.offsetX = 0;
			stageData.offsetY = 0;
			updateStage();
			viewport.focus();
		},
		getDOMElement = () => domElement,
		getSVGContainer = () => svgContainer,
		getStageData = () => Object.assign({}, stageData),
		setStageData = (newStageData) => {
			Object.assign(stageData, newStageData);
			updateStage();
		};
	if (!svgContainer) {
		throw new Error('stage must contain a SVGContainer object');
	}

	Object.freeze(Object.assign(this, {
		stageToViewCoordinates, viewToStageCoordinates, ensureSpaceForPoint, centerViewOn, stagePointAtViewportCenter,
		dropCache: viewport.dropCache, finishScrolling: viewport.finishScrolling, updateStage,
		stagePositionForPointEvent, ensureBoxVisible, ensureSpaceForBox, rescale, setScale, reset,
		getDOMElement, getSVGContainer, getStageData, setStageData
	}));
	updateStage();
};
